import SubHeaders from '@/components/Headers/SubHeaders'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
    createColumns,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { paths } from '@/utils/path'
import dayjs from 'dayjs'
import { Download, Loader2, RotateCw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useWafFreeReport } from '../../hooks/use-waf-free-report'
import {
    getAllWafFreeReport,
    type WaapServiceRow,
    type WafFreeReportParams,
} from '../../services'
import SaleFollowerCell from './sale-follower-cell'

const PER_PAGE = 20

/** Vendor package 4001 là gói miễn phí; nhãn portal của nó là "Basic". */
const FREE_PACKAGE_ID = '4001'

/** Input type=date trả 'YYYY-MM-DD'; BE cần RFC3339 và bỏ qua giá trị sai định dạng. */
const toRfc3339 = (value: string, endOfDay = false) => {
    if (!value) return ''
    return endOfDay ? `${value}T23:59:59Z` : `${value}T00:00:00Z`
}

/** `customer` có thể null (org không tra được bên DC) → fallback organization_id. */
const customerName = (row: WaapServiceRow) =>
    row.customer?.customer_company_name ||
    row.customer?.customer_name ||
    `#${row.organization_id}`

const customerEmail = (row: WaapServiceRow) =>
    row.customer?.customer_email || ''

/** `domains` vắng mặt (omitempty) khi domain_count = 0 — không map thẳng. */
const domainList = (row: WaapServiceRow) => row.domains ?? []

/** `sale_followers` trả null chứ không phải mảng rỗng khi chưa ai theo dõi. */
const saleFollowerNames = (row: WaapServiceRow) =>
    (row.customer?.sale_followers ?? []).map((f) => f.employee_name)

/**
 * Rỗng với dịch vụ không thuộc nhánh Conversant (vd provider Owl/Mlytics): gói là
 * khái niệm của vendor Conversant nên BE trả null. Những dòng này cũng không lọt
 * bộ lọc free lẫn paid, chỉ hiện khi chọn "Tất cả".
 */
const packageLabel = (row: WaapServiceRow) =>
    row.waap_package_id === FREE_PACKAGE_ID
        ? 'FREE'
        : row.waap_package_name || row.waap_package_id || ''

const WafFreeReport = () => {
    const [pkg, setPkg] = useState<'free' | 'paid' | 'all'>('free')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [page, setPage] = useState(1)
    const [isExporting, setIsExporting] = useState(false)
    // Gán xong thì cập nhật tại chỗ: enricher của @mgmt cache theo customer_id
    // (~15 phút), refetch ngay vẫn ra bản cũ.
    const [justAssigned, setJustAssigned] = useState<Record<number, string>>({})

    const params: WafFreeReportParams = useMemo(
        () => ({
            package: pkg === 'all' ? '' : pkg,
            status: 'active',
            from: toRfc3339(from),
            to: toRfc3339(to, true),
            page,
            per_page: PER_PAGE,
        }),
        [pkg, from, to, page]
    )

    const { data, isLoading, isFetching, refetch } = useWafFreeReport(params)

    const rows = data?.results ?? []
    const pagination = data?.pagination
    const total = pagination?.total ?? 0
    const totalPages = pagination?.total_pages ?? 1

    // Phân khúc cho sales: chưa trỏ = hướng dẫn onboarding, đã trỏ = upsell.
    const daTro = rows.filter((r) => r.domain_count > 0).length
    const chuaTro = rows.length - daTro

    const followersOf = (row: WaapServiceRow) => {
        const names = saleFollowerNames(row)
        if (names.length > 0) return names
        const added = row.customer?.customer_id
            ? justAssigned[row.customer.customer_id]
            : undefined
        return added ? [added] : []
    }

    const resetPageThen = (fn: () => void) => {
        setPage(1)
        fn()
    }

    const columns = useMemo(
        () =>
            createColumns<WaapServiceRow>([
                {
                    key: 'id_name',
                    title: 'ID - Tên',
                    width: 260,
                    render: (_, row) => (
                        <span>
                            <span className="font-medium">
                                {row.organization_id}
                            </span>
                            <span className="text-muted-foreground"> - </span>
                            {customerName(row)}
                        </span>
                    ),
                },
                {
                    key: 'sale_followers',
                    title: 'Sales follow',
                    width: 280,
                    render: (_, row) => (
                        <SaleFollowerCell
                            customerId={row.customer?.customer_id}
                            names={followersOf(row)}
                            onAssigned={(name) =>
                                row.customer?.customer_id &&
                                setJustAssigned((prev) => ({
                                    ...prev,
                                    [row.customer!.customer_id]: name,
                                }))
                            }
                        />
                    ),
                },
                {
                    key: 'email',
                    title: 'Email',
                    width: 240,
                    render: (_, row) =>
                        customerEmail(row) || (
                            <span className="text-muted-foreground">—</span>
                        ),
                },
                {
                    key: 'package',
                    title: 'Gói',
                    width: 130,
                    render: (_, row) => {
                        const label = packageLabel(row)
                        if (!label) {
                            return (
                                <span
                                    className="text-muted-foreground"
                                    title={`${row.provider?.name ?? 'Nhà cung cấp này'} không trả thông tin gói`}
                                >
                                    —
                                </span>
                            )
                        }
                        return (
                            <Badge
                                variant={
                                    row.waap_package_id === FREE_PACKAGE_ID
                                        ? 'outline-info'
                                        : 'outline'
                                }
                            >
                                {label}
                            </Badge>
                        )
                    },
                },
                {
                    key: 'domain',
                    title: 'Domain',
                    render: (_, row) => {
                        if (row.domain_count === 0) {
                            return <Badge variant="outline-warning">CHƯA</Badge>
                        }
                        const domains = domainList(row)
                        const hidden = row.domain_count - domains.length
                        return (
                            <span className="break-all text-sm">
                                {domains.map((d) => d.name).join(', ')}
                                {hidden > 0 && (
                                    <span className="text-muted-foreground">
                                        {' '}
                                        (+{hidden})
                                    </span>
                                )}
                            </span>
                        )
                    },
                },
            ]),
        // followersOf doc justAssigned nen phai nam trong deps
        [justAssigned]
    )

    const handleExport = async () => {
        setIsExporting(true)
        try {
            const all = await getAllWafFreeReport({
                ...params,
                page: undefined,
            })
            const XLSX = await import('xlsx')
            const sheet = XLSX.utils.json_to_sheet(
                all.map((row) => ({
                    'Customer ID': row.organization_id,
                    'Tên khách': customerName(row),
                    'Sales follow': followersOf(row).join(', ') || 'Chưa có',
                    Email: customerEmail(row) || '—',
                    Gói: packageLabel(row) || '—',
                    Domain:
                        row.domain_count === 0
                            ? 'CHƯA'
                            : domainList(row)
                                  .map((d) => d.name)
                                  .join(', '),
                    'Số domain': row.domain_count,
                    'Ngày kích hoạt': dayjs(row.created_at).format(
                        'DD/MM/YYYY HH:mm'
                    ),
                }))
            )
            const book = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(book, sheet, 'WAF FREE')
            XLSX.writeFile(
                book,
                `waf-free-report-${dayjs().format('YYYYMMDD-HHmm')}.xlsx`
            )
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    { title: 'Báo cáo', path: paths.toolsWafFreeReport },
                    {
                        title: 'Báo cáo WAF FREE',
                        path: paths.toolsWafFreeReport,
                    },
                ]}
            />

            <div className="space-y-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="space-y-1">
                                <label className="text-sm text-muted-foreground">
                                    Gói
                                </label>
                                <Select
                                    value={pkg}
                                    onValueChange={(v) =>
                                        resetPageThen(() =>
                                            setPkg(v as typeof pkg)
                                        )
                                    }
                                >
                                    <SelectTrigger className="w-40">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="free">
                                            FREE
                                        </SelectItem>
                                        <SelectItem value="paid">
                                            Trả phí
                                        </SelectItem>
                                        <SelectItem value="all">
                                            Tất cả
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm text-muted-foreground">
                                    Kích hoạt từ
                                </label>
                                <Input
                                    type="date"
                                    className="w-44"
                                    value={from}
                                    onChange={(e) =>
                                        resetPageThen(() =>
                                            setFrom(e.target.value)
                                        )
                                    }
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm text-muted-foreground">
                                    đến
                                </label>
                                <Input
                                    type="date"
                                    className="w-44"
                                    value={to}
                                    onChange={(e) =>
                                        resetPageThen(() =>
                                            setTo(e.target.value)
                                        )
                                    }
                                />
                            </div>

                            <Button
                                variant="outline"
                                onClick={() => refetch()}
                                disabled={isFetching}
                            >
                                {isFetching ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <RotateCw className="mr-2 h-4 w-4" />
                                )}
                                Tải lại
                            </Button>

                            <Button
                                onClick={handleExport}
                                disabled={isExporting || total === 0}
                            >
                                {isExporting ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="mr-2 h-4 w-4" />
                                )}
                                Xuất Excel
                            </Button>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-6 text-sm">
                            <span>
                                Tổng:{' '}
                                <strong className="text-foreground">
                                    {total}
                                </strong>
                            </span>
                            <span className="text-success">
                                Đã trỏ domain (trang này):{' '}
                                <strong>{daTro}</strong>
                            </span>
                            <span className="text-warning">
                                Chưa trỏ (trang này): <strong>{chuaTro}</strong>
                            </span>
                        </div>
                    </CardContent>
                </Card>

                <DataTableCustomized<WaapServiceRow>
                    columns={columns}
                    rows={rows}
                    rowKey={(row) => row.id}
                    loading={isLoading}
                    isFetching={isFetching}
                    emptyMessage="Không có dịch vụ nào khớp bộ lọc"
                    bordered
                    autoWidth
                />

                {totalPages > 1 && (
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page <= 1 || isFetching}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            Trước
                        </Button>
                        <span className="text-sm text-muted-foreground">
                            Trang {page}/{totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages || isFetching}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            Sau
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default WafFreeReport
